import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import PageHeader from 'components/PageHeader';
import { MoreVertOutlined as MoreVertOutlinedIcon } from 'components/icons';
import KebabMenu, { MenuItem } from 'components/navigation/KebabMenu';
import {
    MetaItem,
    OUTLINED_ACTION_CLASS,
    PRIMARY_ACTION_CLASS,
    RecordHeader,
    RecordMoreMenu,
} from 'components/record-header/RecordHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { ContractMeta } from 'pages/contracts/ContractPageHeader';
import { getAppliedInOffice } from 'pages/contracts/util';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'store';
import {
    invalidateDeletedClientIdentification,
    useReassessClientIdentificationMutation,
} from 'store/api/client-identifications';
import { useDeleteContractMutation } from 'store/api/contracts';
import { ClassifierData } from 'types/classifiers';
import {
    AssessmentStatusShown,
    ClientIdentificationDto,
    ClientIdentificationPanelDto,
} from 'types/clientIdentification';
import { Contract } from 'types/contracts';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useGoBack, useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import { MarkAsReceivedDialog } from './MarkAsReceivedDialog';
import { RevertReceiptDialog } from './RevertReceiptDialog';
import { useClientIdentificationPopup } from './clientIdentificationPopup';

const STATUS_CHIP: Record<AssessmentStatusShown, ChipType> = {
    DRAFT: 'outlined',
    RECEIVED: 'success',
    SUPERSEDED: 'inactive',
};

interface Props {
    contract: Contract;
    assessment: ClientIdentificationDto;
    panel: ClientIdentificationPanelDto | undefined;
    classifiers: Record<string, ClassifierData>;
}

/**
 * The client identification's name, status and actions. Which actions show is the backend's say alone (`mayDelete`,
 * `mayReceive`, `mayRevert`, `mayStartNewVersion`): nothing here recomputes the rules.
 */
export function ClientIdentificationHeader({ contract, assessment, panel, classifiers }: Props) {
    const isNew = useIsNewDesign();
    const navigate = useNavigate();
    const goBack = useGoBack('/contracts');
    const leaveDeleted = useLeaveDeletedEntity('/contracts');
    const { openPopup: openModal, closePopup: closeModal } = useClientIdentificationPopup();
    const dispatch = useDispatch();
    const [deleteContract] = useDeleteContractMutation();
    const [reassess] = useReassessClientIdentificationMutation();

    const confirmDelete = () =>
        openModal(
            <ConfirmationModal
                titleId="client-identification.delete-title"
                titleValues={{ name: contract.contractName }}
                confirmId="delete"
                content={<p className="text-body1 m-0">{t('client-identification.delete-body')}</p>}
                onCancel={closeModal}
                onConfirm={() => {
                    void deleteContract({ id: String(contract.id) })
                        .unwrap()
                        .then(() => {
                            dispatch(invalidateDeletedClientIdentification(assessment.supersedesContractId));
                            leaveDeleted();
                        })
                        .catch(() => undefined)
                        .finally(closeModal);
                }}
            />
        );

    const confirmNewVersion = () =>
        openModal(
            <ConfirmationModal
                titleId="client-identification.new-version-title"
                titleValues={{ name: contract.contractName }}
                confirmId="client-identification.new-version"
                content={<p className="text-body1 m-0">{t('client-identification.new-version-body')}</p>}
                onCancel={closeModal}
                onConfirm={() => {
                    void reassess({ id: contract.id, tierCode: null })
                        .unwrap()
                        .then(({ contractId }) => {
                            closeModal();
                            navigate(`/contract/${contractId}`);
                        })
                        .catch(() => undefined);
                }}
            />
        );

    const openReceive = (loadedPanel: ClientIdentificationPanelDto) =>
        openModal(<MarkAsReceivedDialog contractId={contract.id} panel={loadedPanel} onClose={closeModal} />);
    const openRevert = () => openModal(<RevertReceiptDialog contractId={contract.id} onClose={closeModal} />);

    const outlined = () => buttonStyle({ variant: 'outlined', size: 'small' }, cn(isNew && OUTLINED_ACTION_CLASS));
    const deleteItems: MenuItem[] = assessment.mayDelete
        ? [{ title: t('delete'), handler: confirmDelete, variant: 'destructive' }]
        : [];
    const actions = (
        <>
            {!isNew && deleteItems.length ? (
                <KebabMenu menuItems={deleteItems} icon={<MoreVertOutlinedIcon fontSize="medium" />} />
            ) : null}
            {assessment.mayRevert ? (
                <Button {...outlined()} onClick={openRevert}>
                    <FormattedMessage id="client-identification.revert" />
                </Button>
            ) : null}
            {assessment.mayStartNewVersion ? (
                <Button {...outlined()} onClick={confirmNewVersion}>
                    <FormattedMessage id="client-identification.new-version" />
                </Button>
            ) : null}
            {assessment.mayReceive ? (
                <Button
                    {...buttonStyle(
                        { variant: 'contained', color: 'secondary', size: 'small' },
                        cn(isNew && PRIMARY_ACTION_CLASS)
                    )}
                    // What is still missing is listed in the dialog, so it opens only once the panel has said.
                    disabled={!panel}
                    focusableWhenDisabled
                    onClick={() => (panel ? openReceive(panel) : undefined)}
                >
                    <FormattedMessage id="client-identification.mark-as-received" />
                </Button>
            ) : null}
            {isNew ? <RecordMoreMenu menuItems={deleteItems} /> : null}
        </>
    );
    const statusChip = (
        <StatusChip
            type={STATUS_CHIP[assessment.statusShown]}
            label={t(`client-identification.status.${assessment.statusShown}`)}
        />
    );
    const typeName = getClassifierText(classifiers['CONTRACT_TYPE'], contract.contractTypeCode);

    if (isNew) {
        return (
            <RecordHeader
                chips={statusChip}
                title={contract.contractName}
                pin={{ kind: 'contract', id: contract.id }}
                meta={
                    <>
                        <MetaItem label={<FormattedMessage id="contracts.type" />}>{typeName}</MetaItem>
                        <MetaItem label={<FormattedMessage id="contracts.applied-in" />}>
                            {getAppliedInOffice(contract)}
                        </MetaItem>
                    </>
                }
                actions={actions}
            />
        );
    }
    return (
        <PageHeader
            title={contract.contractName}
            showBreadcrumb
            onBack={goBack}
            buttons={<div className="flex items-center justify-end gap-2">{actions}</div>}
        >
            <div className="flex flex-wrap items-center gap-2 pt-4 sm:gap-4">
                {statusChip}
                <ContractMeta contract={contract} classifiers={classifiers} />
            </div>
        </PageHeader>
    );
}
